import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Permission } from '@core/enums/permissions';
import { RatingViewVersionEnum } from '@core/enums/rating-view-version.enum';
import { TableColumnType } from '@core/enums/table';
import { IAgeGradeInstrument } from '@core/interfaces/iage-grade-instrument';
import { ISites } from '@core/interfaces/isites';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { StateService } from '@core/services/state.service';
import { StudentClassEnrollmentService } from '@core/services/student-class-enrollment.service';
import { ToastService } from '@core/services/toast.service';
import { FilterSelectComponent } from '@shared/components/dropdowns/filter-select/filter-select.component';
import { take } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-total-locked-children',
    templateUrl: './total-locked-children.component.html',
    styleUrls: ['./total-locked-children.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TotalLockedChildrenComponent implements OnInit {
  @Output() formValues = new EventEmitter();
  lockedForm: FormGroup | any;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  pageSizeOptions: number[] = [10, 25, 50, 100];
  pageSize: number = 10;
  pageIndex: number = 1;
  tableData?: any;
  tableColumns: any[] = [];
  totalData: number = 0;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();

  @ViewChild('ageGroupInstrumentSelect') ageGroupInstrumentSelect:
    | FilterSelectComponent
    | undefined;
  @ViewChild('ratingPeriodSelect') ratingPeriodSelect:
    | FilterSelectComponent
    | undefined;
  @ViewChild('stateSelect') stateSelect: FilterSelectComponent | undefined;
  @ViewChild('agencySelect') agencySelect: FilterSelectComponent | undefined;
  @ViewChild('sitesSelect') sitesSelect: FilterSelectComponent | undefined;

  user = this.auth.getCurrentUser();
  hasAccessToAll: boolean = this.permissionService.checkPermission(Permission.AccessAllAgencies);
  accessAssignedStateAgencies!: boolean;

  initStateId = this.user.stateId;
  initAgencyId = this.user.agencyId;

  clearDropdown = false;
  clearState = false;
  clearAgency = false;
  searchPayload: any;
  ageGradeInstruments?: IAgeGradeInstrument[] = [];
  sites!: ISites[];
  isSoftImplementation: boolean = false;
  agencySoftImplementationEnabled: boolean = false;
  siteSoftImplementationEnabled: boolean = false;
  classroomSoftImplementationEnabled: boolean = false;
  isFieldStudyEnabledRatingPeriod: boolean = false;
  selectedSchoolYearId: number | null = null;

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private permissionService: PermissionService,
    private toastService: ToastService,
    private router: Router,
    private studentClassEnrollmentService: StudentClassEnrollmentService,
    private stateService: StateService,
    private translocoService: TranslocoService
  ) {
    this.user = this.auth.getCurrentUser();
    this.hasAccessToAll = this.permissionService.checkPermission(Permission.AccessAllAgencies);
    this.accessAssignedStateAgencies = this.permissionService.checkPermission(Permission.AccessAssignedStateAgencies);
  }

  // TODO: remove this when ratings for 2026-27 school year are fully activated
  ratingViewVersion = RatingViewVersionEnum.VERSION_2015;

  checkStateConfiguration(): void {
    if (this.stateId?.value) {
      this.stateService.getStatesById(this.stateId.value).pipe(
        take(1)
      ).subscribe((state: any) => {
        this.ratingViewVersion = state.ratingViewVersionId ?? RatingViewVersionEnum.VERSION_2015;
      });
    }
  }

  get stateId() {
    return this.lockedForm.get('stateId');
  }
  get agencyId() {
    return this.lockedForm.get('agencyId');
  }
  get ageGradeInstrumentId() {
    return this.lockedForm.get('ageGradeInstrumentId');
  }
  get classId() {
    return this.lockedForm.get('classId');
  }
  get classroomId() {
    return this.lockedForm.get('classroomId');
  }
  get ratingPeriodId() {
    return this.lockedForm.get('ratingPeriodId');
  }
  get siteId() {
    return this.lockedForm.get('siteId');
  }

  ngOnInit(): void {
    this.initializeForm();

    this.paginateTable(true);
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.paginator._intl.itemsPerPageLabel = '';
  }

  initializeForm(): void {
    this.lockedForm = this.fb.group({
      userId: [this.user.userId ?? null],
      stateId: [this.user.stateId ?? null, [Validators.required]],
      agencyId: [this.user.agencyId ?? null, [Validators.required]],
      ratingPeriodId: [null],
      siteId: [null],
      classId: [null],
      ageGradeInstrumentId: [null],
    });

    this.checkStateConfiguration();
  }

  handleState(state: any) {
    this.stateId.setValue(state?.id);
    this.agencyId.setValue(null);
    this.checkStateConfiguration();
  }

  handleAgency(agency: any): void {
    this.agencyId.setValue(agency?.id);
    this.classId.setValue(null);
    this.ratingPeriodId.setValue(null);
    this.agencySoftImplementationEnabled = agency?.isSoftImplementation ?? agency?.item?.isSoftImplementation ?? false;    
    this.isFieldStudyEnabledRatingPeriod = false;
    this.ageGradeInstrumentId.setValue(null);
    this.ageGroupInstrumentSelect?.clearSelected();
    this.updateSoftImplementation();
  }

  handleRatingPeriod(ratingPeriod: any): void {
    this.ratingPeriodId.setValue(ratingPeriod?.id);
    this.ageGradeInstrumentId.setValue(null);
    this.ageGroupInstrumentSelect?.clearSelected();
    this.isFieldStudyEnabledRatingPeriod = ratingPeriod?.globalRatingPeriod?.isPilotEnabled ?? false;
    this.selectedSchoolYearId = ratingPeriod?.schoolYearId ?? null;
  }

  handleSite(site: any): void {
    this.siteId.setValue(site?.id);
    this.siteSoftImplementationEnabled = site?.isSoftImplementation ?? site?.item?.isSoftImplementation ?? false;
    this.updateSoftImplementation();
  }

  handleChosenClassroom(classroom: any): void {
    this.classId.setValue(classroom?.id);
    this.classroomSoftImplementationEnabled = classroom?.isSoftImplementation ?? classroom?.item?.isSoftImplementation ?? false;
    this.updateSoftImplementation();
  }

  updateSoftImplementation(): void {
    this.isSoftImplementation = this.classroomSoftImplementationEnabled 
      || this.siteSoftImplementationEnabled 
      || this.agencySoftImplementationEnabled;
  }

  handleAgeGrade(event: IAgeGradeInstrument): void {
    this.ageGradeInstrumentId.setValue(event?.id);
  }

  search() {
    if (this.lockedForm.invalid) {
      this.toastService.error(this.translocoService.translate('user.dashboard.lockedChildren.requiredFieldsToast'));
      return;
    }

    this.paginateTable(true);
  }

  paginateTable(resetPage?: boolean): void {
    const searchPayload = this.createSearchPayload(resetPage);
    this.getAgencyCurrentStudentEnrollments(searchPayload);
  }

  createSearchPayload(resetPage?: boolean): void {
    const payload = this.lockedForm.value;

    payload.userId = this.user.userId ?? null;
    payload.stateId = this.stateId.value ?? null;
    payload.agencyId = this.agencyId.value ?? null;
    payload.ratingPeriodId = this.ratingPeriodId.value ?? null;
    payload.siteId = this.siteId.value ?? null;
    payload.classId = this.classId.value ?? null;
    payload.ageGradeInstrumentId = this.ageGradeInstrumentId.value ?? null;
    payload.pageIndex = resetPage ? 1 : this.paginator.pageIndex + 1;
    payload.itemsPerPage = resetPage ? this.pageSize : this.paginator.pageSize;

    if (resetPage && this.paginator) {
      this.paginator.pageSize = payload.itemsPerPage;
    }

    return payload;
  }

  getAgencyCurrentStudentEnrollments(params: any): void {
    this.studentClassEnrollmentService
      .getAgencyCurrentStudentEnrollments(params)
      .pipe(take(1))
      .subscribe(
        (res: any) => {
          this.tableData = res.items.map((item: any) => ({
            ...item,
            userId: item.userId ?? null,
            stateId: item.stateId ?? null,
            agencyId: item.agencyId ?? null,
            ratingPeriodId: item.ratingPeriodId ?? null,
            siteId: item.siteId ?? null,
            classId: item.classId ?? null,
            ageGradeInstrumentId: item.ageGradeInstrumentId ?? null,
          }));

          this.dataSource = new MatTableDataSource<any>(this.tableData);
          this.initializeTableData(res);
        }
      );
  }

  initializeTableData(res: any): void {
    this.initializeTableColumns(res.items);
    this.dataSource = new MatTableDataSource(res.items);
    this.totalData = res.totalData;
    this.tableData = res.items;
  }

  initializeTableColumns(_items: any[]): void {
    this.tableColumns = [
      {
        columnDef: 'studentClassEnrollmentId',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.ratings'),
        icon: 'pen',
        type: TableColumnType.actionIcon,
        action: 'view-rating',
        colorClass: 'text-drdpblue-300',
        cursorClass: 'cursor-pointer',
      },
      {
        columnDef: 'studentName',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.studentName'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'enrollment',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.enrollment'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'ratingPeriodName',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.ratingPeriod'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'classroomName',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.classroom'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'siteName',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.site'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'assessmentDate',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.assessmentDate'),
        type: TableColumnType.date,
        isSortable: true,
      },
      {
        columnDef: 'lockingDate',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.lockDate'),
        type: TableColumnType.date,
        isSortable: true,
      },
      {
        columnDef: 'isLocked',
        header: this.translocoService.translate('user.dashboard.lockedChildren.table.lockingStatus'),
        type: TableColumnType.boolIcon,
        truthyClass: 'text-drdpGreen-300',
        truthyIconUrl: 'assets/images/misc/lock-alt.svg',
        falsyClass: 'text-drdpgrey-200',
        falsyIconUrl: 'assets/images/misc/padlock.svg',
        isSortable: true,
      },
    ];
  }

  onIconClick(event: any): void {
    if (event.action == 'view-rating') {
      this.router.navigate([`input-ratings/${event.data.studentClassEnrollmentId}`]);
    }
  }

  clearTable(): void {
    const empty: any = [];
    this.dataSource = new MatTableDataSource(empty);
    this.totalData = 0;
    this.tableData = [];
  }

  clear(): void {
    this.sitesSelect?.clearSelected();
    this.ageGroupInstrumentSelect?.clearSelected();
    this.clearDropdown = true;
    this.ratingPeriodSelect?.clearSelected();
    this.ratingPeriodId.setValue(null);
    this.selectedSchoolYearId = null;
    this.clearTable();

    if (!this.stateSelect?.disabled) {
      this.initStateId = 0;
      this.stateId.setValue(null);
      this.clearState = true;
    }

    if (!this.agencySelect?.disabled) {
      this.initAgencyId = 0;
      this.agencyId.setValue(null);
      this.clearAgency = true;
    }
  }
}
